import React, { useCallback, useEffect, useMemo, useState } from 'react';
import './Overview.css';
import { getLandlordAnalytics } from '../../../api/services/analyticsService';

const safeNumber = (value) => (Number.isFinite(Number(value)) ? Number(value) : 0);

const currency = (value) => {
  const num = safeNumber(value);
  return `KES ${num.toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
};

const Overview = ({ onNavigate }) => {
  const [snapshot, setSnapshot] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchSnapshot = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const resp = await getLandlordAnalytics();
      setSnapshot(resp?.data || null);
    } catch (e) {
      setError(e?.message || 'Failed to load analytics');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSnapshot();
  }, [fetchSnapshot]);

  const derived = useMemo(() => {
    const s = snapshot || {};
    return {
      propertiesCount: safeNumber(s.propertiesCount),
      unitsCount: safeNumber(s.unitsCount),
      activeTenantsCount: safeNumber(s.activeTenantsCount),
      agentsAssignedCount: safeNumber(s.agentsAssignedCount),
      rentPaidThisMonth: safeNumber(s.rentPaidThisMonth),
      rentPendingThisMonth: safeNumber(s.rentPendingThisMonth),
      totalMonthlyPayableRent: safeNumber(s.totalMonthlyPayableRent),
      maintenanceOpenCount: safeNumber(s.maintenanceOpenCount),
    };
  }, [snapshot]);

  return (
    <div className="overview-container">
      {/* Welcome Section */}
      <div className="welcome-banner">
        <div className="welcome-content">
          <h1 className="welcome-title">Hello, welcome back! 👋</h1>
          <p className="welcome-subtitle">Here's your property management overview. Stay on top of your rentals, track payments, and grow your portfolio with confidence.</p>
        </div>
      </div>
      
      <h1 className="page-title">Dashboard</h1>

      {/* Key Metrics Cards */}
      <div className="metrics-row">
        <div className={`metric-card ${loading ? 'metric-card-skeleton' : ''}`}>
          <div className="metric-icon metric-icon-dollar-warning">⚠️</div>
          <div className="metric-content">
            <div className="metric-label">Rent Pending (This Month)</div>
            <div className="metric-value">{currency(derived.rentPendingThisMonth)}</div>
            <div className={`metric-trend ${derived.rentPendingThisMonth > 0 ? 'metric-trend-warning' : 'metric-trend-neutral'}`}>
              <span className="trend-text">{derived.rentPendingThisMonth > 0 ? 'Requires follow-up' : 'All clear'}</span>
            </div>
          </div>
        </div>

        <div className={`metric-card ${loading ? 'metric-card-skeleton' : ''}`}>
          <div className="metric-icon metric-icon-dollar">💰</div>
          <div className="metric-content">
            <div className="metric-label">Rent Paid (This Month)</div>
            <div className="metric-value">{currency(derived.rentPaidThisMonth)}</div>
            <div className="metric-trend metric-trend-neutral">
              <span className="trend-text">Collected so far</span>
            </div>
          </div>
        </div>

        <div className={`metric-card ${loading ? 'metric-card-skeleton' : ''}`}>
          <div className="metric-icon metric-icon-house">🏢</div>
          <div className="metric-content">
            <div className="metric-label">Properties</div>
            <div className="metric-value">{derived.propertiesCount}</div>
            <div className="metric-trend metric-trend-neutral">
              <span className="trend-text">Total properties</span>
            </div>
          </div>
        </div>

        <div className={`metric-card ${loading ? 'metric-card-skeleton' : ''}`}>
          <div className="metric-icon metric-icon-check">🏠</div>
          <div className="metric-content">
            <div className="metric-label">Units</div>
            <div className="metric-value">{derived.unitsCount}</div>
            <div className="metric-trend metric-trend-neutral">
              <span className="trend-text">Total units</span>
            </div>
          </div>
        </div>

        <div className={`metric-card ${loading ? 'metric-card-skeleton' : ''}`}>
          <div className="metric-icon metric-icon-shield">🔧</div>
          <div className="metric-content">
            <div className="metric-label">Maintenance Open</div>
            <div className="metric-value">{derived.maintenanceOpenCount}</div>
            <div className={`metric-trend ${derived.maintenanceOpenCount > 0 ? 'metric-trend-warning' : 'metric-trend-neutral'}`}>
              <span className="trend-text">{derived.maintenanceOpenCount > 0 ? 'Needs attention' : 'No open requests'}</span>
            </div>
          </div>
        </div>

        <div className={`metric-card ${loading ? 'metric-card-skeleton' : ''}`}>
          <div className="metric-icon metric-icon-house">🏠</div>
          <div className="metric-content">
            <div className="metric-label">Active Tenants</div>
            <div className="metric-value">{derived.activeTenantsCount}</div>
            <div className="metric-trend metric-trend-neutral">
              <span className="trend-text">Currently active</span>
            </div>
          </div>
        </div>
      </div>

      {error ? (
        <div className="landlord-overview-alert landlord-overview-alert-error">
          <div className="landlord-overview-alert-title">Couldn’t load analytics</div>
          <div className="landlord-overview-alert-message">{error}</div>
          <button className="landlord-overview-retry-btn" onClick={fetchSnapshot}>
            Retry
          </button>
        </div>
      ) : null}

      <div className="content-card">
        <h3 className="content-card-title">Quick Actions</h3>
        <div className="landlord-quick-actions">
          <button className="landlord-quick-btn" onClick={() => onNavigate?.('rent-status')}>
            View Rent Status
          </button>
          <button className="landlord-quick-btn" onClick={() => onNavigate?.('manage-rentals')}>
            Manage Rentals
          </button>
          <button className="landlord-quick-btn landlord-quick-btn-secondary" onClick={() => onNavigate?.('analytics')}>
            Open Analytics
          </button>
        </div>

        <div className="landlord-overview-note">
          Monthly payable rent: <span className="landlord-overview-note-strong">{currency(derived.totalMonthlyPayableRent)}</span>
          {derived.agentsAssignedCount ? (
            <>
              {' '}• Agents assigned: <span className="landlord-overview-note-strong">{derived.agentsAssignedCount}</span>
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default Overview;
